// Typed fetch client over the API (A10). The session is Better Auth's cookie on `api.` — sent with
// `credentials: 'include'`; the API's CORS allows the dashboard origin. Our errors are `{ error }` (+ fields, e.g.
// `missingCents`); they come back as a typed failure, never a throw — a network-level failure too (status 0). Every
// failure carries `diag`: what the error screens show so a failure can be matched in our logs or Cloudflare's.
import { ApiError } from '@req/contracts';
import type { z } from 'zod';

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? 'https://api.requerimento.app.br';

/**
 * What a failed request looked like from the browser. `reachedApi`: the answer carried our `x-served-by` marker; a
 * request blocked before reaching us (or a network failure — the browser can't tell them apart without CORS headers)
 * is status 0 with no cf-ray readable.
 */
export interface FailureDiag {
  method: string;
  path: string; // without the query string (it may hold a CPF or a name)
  status: number;
  cfRay: string | null;
  reachedApi: boolean;
  at: string; // ISO timestamp
}

export type ApiFailure = { ok: false; status: number; error: string; body: Record<string, unknown>; diag: FailureDiag };
export type ApiResult<T> = { ok: true; status: number; data: T } | ApiFailure;

export interface ApiClient {
  get<T>(path: string, schema?: z.ZodType<T>): Promise<ApiResult<T>>;
  post<T>(path: string, body?: unknown, schema?: z.ZodType<T>): Promise<ApiResult<T>>;
  put<T>(path: string, body?: unknown, schema?: z.ZodType<T>): Promise<ApiResult<T>>;
  del<T>(path: string, schema?: z.ZodType<T>): Promise<ApiResult<T>>;
}

/** The customer the session acts for (A10: customer routes read `x-customer-id`); set by the session layer. */
let activeCustomerId: string | null = null;
export const setActiveCustomerId = (id: string | null): void => {
  activeCustomerId = id;
};
/** For requests made outside this client (map tiles of a module-gated layer). */
export const activeCustomer = (): string | null => activeCustomerId;

export function createApiClient(opts: { baseUrl?: string; fetch?: typeof fetch; customerId?: () => string | null } = {}): ApiClient {
  const base = (opts.baseUrl ?? API_BASE_URL).replace(/\/$/, '');
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const customerOf = opts.customerId ?? (() => activeCustomerId);

  async function call<T>(method: string, path: string, body: unknown, schema?: z.ZodType<T>): Promise<ApiResult<T>> {
    const customer = customerOf();
    const diagOf = (status: number, res?: Response): FailureDiag => ({
      method, path: path.split('?')[0]!, status,
      cfRay: res?.headers.get('cf-ray') ?? null,
      reachedApi: res?.headers.get('x-served-by') === 'requerimento-api',
      at: new Date().toISOString(),
    });
    let res: Response;
    try {
      res = await doFetch(`${base}${path}`, {
        method,
        credentials: 'include',
        headers: {
          accept: 'application/json',
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(customer ? { 'x-customer-id': customer } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch (e) {
      // fetch's own failure (network down, or an answer without our CORS headers — the browser hides it entirely)
      if (!(e instanceof TypeError)) throw e;
      return { ok: false, status: 0, error: 'network', body: {}, diag: diagOf(0) };
    }
    const text = await res.text();
    if (!res.ok) {
      let json: unknown = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        /* not ours (an HTML error page): http_<status> below */
      }
      const parsed = ApiError.safeParse(json);
      return {
        ok: false,
        status: res.status,
        error: parsed.success ? parsed.data.error : `http_${res.status}`,
        body: json && typeof json === 'object' ? (json as Record<string, unknown>) : {},
        diag: diagOf(res.status, res),
      };
    }
    const json: unknown = text ? JSON.parse(text) : null;
    return { ok: true, status: res.status, data: schema ? schema.parse(json) : (json as T) };
  }

  return {
    get: (path, schema) => call('GET', path, undefined, schema),
    post: (path, body, schema) => call('POST', path, body ?? {}, schema),
    put: (path, body, schema) => call('PUT', path, body ?? {}, schema),
    del: (path, schema) => call('DELETE', path, undefined, schema),
  };
}

export const api = createApiClient();
